import { convexQuery } from "@convex-dev/react-query";
import { api } from "@selca/backend/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@workspace/ui/components/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import {
  BarChart3Icon,
  ChevronUpIcon,
  HomeIcon,
  ImageIcon,
  LogOutIcon,
  MessageCircleIcon,
  MoonIcon,
  PanelLeftIcon,
  SettingsIcon,
  SunIcon,
} from "lucide-react";
import { useNavigation } from "../App";
import { useAuth } from "../hooks/use-auth";
import { useTheme } from "../hooks/use-theme";
import { authClient } from "../lib/auth-client";
import selcaIcon from "../selca-icon-min.png";
import selcaIconBrown from "../selca-icon-min-brown.png";
import { useClientStore } from "../store";

const menuItems = [
  {
    title: "Home",
    icon: HomeIcon,
    path: "/",
  },
  {
    title: "Images",
    icon: ImageIcon,
    path: "/images",
  },
  {
    title: "Metrics",
    icon: BarChart3Icon,
    path: "/metrics",
  },
  {
    title: "Image Playground",
    icon: MessageCircleIcon,
    path: "/image-playground",
  },
];

function UserProfile() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const { navigate } = useNavigation();
  const { isMobile } = useClientStore();
  const { sidebarOpen } = useClientStore();
  const isCollapsed = !sidebarOpen;

  if (!user) return null;

  // better-auth user image is usually the social one if available
  const userImage = user.image || `https://avatar.vercel.sh/${user.id}`;

  const handleSignOut = async () => {
    await authClient.signOut();
    await authClient.getSession();
    queryClient.clear();
    await new Promise((resolve) => setTimeout(resolve, 150));
    navigate("/");
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              className="h-12 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              size="lg"
              tooltip={isCollapsed ? user.name || "Profile" : undefined}
            >
              <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-sidebar-border">
                <img
                  alt={user.name || "User"}
                  className="size-full object-cover"
                  src={userImage}
                />
              </div>
              {!isCollapsed && (
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user.name || "User"}
                  </span>
                  <span className="truncate text-muted-foreground text-xs">
                    {user.email || profile?.phoneNumber}
                  </span>
                </div>
              )}
              {!isCollapsed && (
                <ChevronUpIcon className="ml-auto size-4 opacity-50" />
              )}
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            align={isMobile ? "center" : "end"}
            className="w-48 p-1"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <button
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-destructive text-sm transition-colors hover:bg-destructive/10"
              onClick={handleSignOut}
              type="button"
            >
              <LogOutIcon className="size-4" />
              <span>Sign Out</span>
            </button>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function SidebarDecorations() {
  const { state } = useSidebar();

  return (
    <>
      {/* Slim top bar that extends from the sidebar */}
      <div
        className={cn(
          "fixed top-0 z-30 hidden h-4 transition-[left] duration-200 ease-linear md:block",
          "bg-sidebar",
          state === "collapsed"
            ? "left-[calc(var(--sidebar-width-icon)-1px)]"
            : "left-[calc(var(--sidebar-width)-1px)]",
          "right-0"
        )}
      >
        {/* Bottom border line - positioned to connect with curved decoration */}
        <div className="absolute bottom-0 left-4 right-0 h-px bg-sidebar-border" />
      </div>

      <div className="pointer-events-none absolute inset-y-0 -right-8 z-50 hidden w-8 md:block">
        {/* Top Corner - Concave curve at junction of top bar and content */}
        <div className="absolute top-4 left-0 h-4 w-4">
          <svg
            className="size-full"
            preserveAspectRatio="none"
            viewBox="0 0 16 16"
          >
            <path
              className="fill-sidebar"
              d="M 0 0 L 16 0 A 16 16 0 0 0 0 16 Z"
            />
            <path
              className="stroke-sidebar-border"
              d="M 16 0 A 16 16 0 0 0 0 16"
              fill="none"
              strokeWidth="1"
            />
          </svg>
        </div>

        {/* Bottom Corner - Concave curve at bottom of sidebar */}
        <div className="absolute bottom-0 left-0 h-8 w-8">
          <svg
            className="size-full"
            preserveAspectRatio="none"
            viewBox="0 0 32 32"
          >
            <path
              className="fill-sidebar"
              d="M 0 32 L 32 32 A 32 32 0 0 1 0 0 Z"
            />
            <path
              className="stroke-sidebar-border"
              d="M 32 32 A 32 32 0 0 1 0 0"
              fill="none"
              strokeWidth="1"
            />
          </svg>
        </div>
      </div>
    </>
  );
}

export function AppSidebar({ isHomePage = false }: { isHomePage?: boolean }) {
  const { state } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const { sidebarOpen, toggleSidebar, isMobile } = useClientStore();
  const { path, navigate } = useNavigation();
  const isCollapsed = !sidebarOpen;

  return (
    <Sidebar
      className={cn(
        "group/sidebar border-r-sidebar-border bg-sidebar",
        isHomePage && "opacity-60 hover:opacity-70"
      )}
      collapsible="icon"
      style={
        isHomePage
          ? {
              backgroundColor:
                "color-mix(in srgb, var(--sidebar), transparent 10%)",
            }
          : undefined
      }
    >
      <SidebarDecorations />
      <SidebarHeader className="border-sidebar-border border-b p-2 -mr-1">
        <div className="flex items-center gap-2">
          <img
            alt="Selca"
            className="size-7 shrink-0"
            height={28}
            src={theme === "dark" ? selcaIcon : selcaIconBrown}
            width={28}
          />
          {!isCollapsed && (
            <span
              className="font-bold text-foreground text-xl tracking-wide"
              style={{ fontFamily: "var(--font-display)" }}
            >
              selca
            </span>
          )}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="flex w-full items-center gap-2 rounded-md p-2 text-sm transition-colors hover:bg-sidebar-accent"
              onClick={toggleSidebar}
              type="button"
            >
              <PanelLeftIcon className="size-4 shrink-0" />
              {!isCollapsed && (
                <span className="whitespace-nowrap">Collapse</span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent
            align="center"
            hidden={!isCollapsed || isMobile}
            side="right"
          >
            Expand
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
              className="flex w-full items-center gap-2 rounded-md p-2 text-sm transition-colors hover:bg-sidebar-accent"
              onClick={toggleTheme}
              type="button"
            >
              {theme === "dark" ? (
                <SunIcon className="size-4 shrink-0" />
              ) : (
                <MoonIcon className="size-4 shrink-0" />
              )}
              {!isCollapsed && (
                <span className="whitespace-nowrap">
                  {theme === "dark" ? "Light Mode" : "Dark Mode"}
                </span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent
            align="center"
            hidden={!isCollapsed || isMobile}
            side="right"
          >
            {theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          </TooltipContent>
        </Tooltip>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Panels</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    isActive={path === item.path}
                    onClick={() => navigate(item.path)}
                    tooltip={item.title}
                  >
                    <item.icon />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-sidebar-border border-t p-2 -mr-1">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={path === "/settings"}
              onClick={() => navigate("/settings")}
              tooltip="Settings"
            >
              <SettingsIcon />
              <span>Settings</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <UserProfile />
      </SidebarFooter>
    </Sidebar>
  );
}
